(() => {
  /*
    Patches SparkLayer interface text.

    These strings come from the SparkLayer Core Script translation blocks
    (app.sparklayer.io -> Configuration -> Frontend -> Core), not from
    this theme, so they can't be corrected in Liquid. SparkLayer renders the
    cart/checkout interface client-side and re-renders it as the customer moves
    through the steps, so the text is caught here as it appears.

    Each entry should be fixed at source in the Core Script instead; once an
    entry is corrected there, delete it here. When this list is empty, remove
    the file and its <script> tag in layout/theme.liquid.
  */
  const path = window.location.pathname.toLowerCase();
  const isEnglish = path === '/en' || path.startsWith('/en/');

  if (window.__rdSparkLayerTextFixLoaded) {
    return;
  }

  window.__rdSparkLayerTextFixLoaded = true;

  const greekFixes = [
    /*
      cart.checkout.payment-method.full.upfrontPayment is missing its leading
      "Π". Anchored to the start of the string on purpose: the correct spelling
      "Πληρωμή" *contains* "ληρωμή", so an unanchored replace would rewrite the
      healthy label into "ΠΠληρωμή...".
    */
    [/^(\s*)ληρωμή(?=:)/, '$1Πληρωμή'],
    [/^(\s*)(?:Π|π)?ληρωμή\s*:?\s*με\s+(?:το\s+)?τιμολόγιο\s*$/i, '$1Τραπεζική Κατάθεση'],
    [/^(\s*)(?:Π|π)ληρωμή\s*:?\s*(?:με\s+)?τραπεζική\s+(?:κατάθεση|μεταφορά)\s*$/i, '$1Τραπεζική Κατάθεση'],
    [/^(\s*)τραπεζική\s+κατάθεση\s*$/i, '$1Τραπεζική Κατάθεση'],
    [/^(\s*)τραπεζική\s+μεταφορά\s*$/i, '$1Τραπεζική Κατάθεση'],
    [/^(\s*)(?:Π|π)ληρωμή\s*:?\s*(?:με\s+)?(?:χρεωστική|πιστωτική)?\s*\/?\s*(?:πιστωτική|χρεωστική)?\s*κάρτα\s*$/i, '$1Χρεωστική/Πιστωτική κάρτα'],
    [/^(\s*)(?:χρεωστική|πιστωτική)\s*\/\s*(?:πιστωτική|χρεωστική)\s+κάρτα\s*$/i, '$1Χρεωστική/Πιστωτική κάρτα'],
  ];

  const englishFixes = [
    [/^(\s*)payment\s*:?\s*(?:with\s+)?invoice\s*$/i, '$1Bank transfer'],
    [/^(\s*)payment\s*:?\s*(?:by|via|with)?\s*bank(?:\s+transfer)?\s*$/i, '$1Bank transfer'],
    [/^(\s*)pay(?:ment)?\s+(?:by|via|with)\s+bank(?:\s+transfer)?\s*$/i, '$1Bank transfer'],
    [/^(\s*)bank\s+transfer\s*$/i, '$1Bank transfer'],
    [/^(\s*)upfront\s+payment\s*$/i, '$1Bank transfer'],
    [/^(\s*)payment\s*:?\s*(?:by|via|with)?\s*(?:debit|credit)?\s*\/?\s*(?:credit|debit)?\s*card\s*$/i, '$1Debit/Credit card'],
    [/^(\s*)pay(?:ment)?\s+(?:by|via|with)\s+(?:debit|credit)\s*\/?\s*(?:credit|debit)?\s+card\s*$/i, '$1Debit/Credit card'],
    [/^(\s*)pay(?:ment)?\s+(?:by|via|with)\s+card\s*$/i, '$1Debit/Credit card'],
    [/^(\s*)(?:debit|credit)\s*\/\s*(?:credit|debit)\s+card\s*$/i, '$1Debit/Credit card'],
    [/^(\s*)credit\s+card\s*$/i, '$1Debit/Credit card'],
  ];

  const fixes = isEnglish ? englishFixes : greekFixes;

  const attributeNames = ['aria-label'];
  const ignoredTextParents = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA']);
  const observedRoots = new WeakSet();
  const scheduledRoots = new WeakSet();

  function getPaymentMethodText(type) {
    if (type === 'bank') {
      return isEnglish ? 'Bank transfer' : 'Τραπεζική Κατάθεση';
    }

    return isEnglish ? 'Debit/Credit card' : 'Χρεωστική/Πιστωτική κάρτα';
  }

  function fixString(value) {
    if (typeof value !== 'string' || !value) {
      return value;
    }

    return fixes.reduce((current, [from, to]) => current.replace(from, to), value);
  }

  function fixPaymentMethodLabelsByOrder(root) {
    const labels = Array.from(root.querySelectorAll?.('label') || []).filter((label) =>
      label.querySelector('input[name="payment-method"]')
    );

    if (labels.length < 2) {
      return;
    }

    [
      [labels[0], getPaymentMethodText('bank')],
      [labels[1], getPaymentMethodText('card')],
    ].forEach(([label, paymentLabel]) => {
      label.setAttribute('aria-label', paymentLabel);

      const visibleLabel = label.querySelector('p');

      if (visibleLabel) {
        visibleLabel.textContent = paymentLabel;
      }
    });
  }

  function fixElement(element) {
    attributeNames.forEach((attributeName) => {
      if (!element.hasAttribute?.(attributeName)) {
        return;
      }

      const currentValue = element.getAttribute(attributeName);
      const fixedValue = fixString(currentValue);

      if (fixedValue !== currentValue) {
        element.setAttribute(attributeName, fixedValue);
      }
    });

    /* SparkLayer renders some interfaces into a shadow root, which mutations
       don't bubble out of, so each one needs observing in its own right. */
    if (element.shadowRoot) {
      observeRoot(element.shadowRoot);
    }
  }

  function fixRoot(root) {
    if (!root) {
      return;
    }

    if (root.nodeType === Node.ELEMENT_NODE) {
      fixElement(root);
    }

    root.querySelectorAll?.('*').forEach(fixElement);
    fixPaymentMethodLabelsByOrder(root);

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;

        if (!node.nodeValue?.trim() || !parent || ignoredTextParents.has(parent.tagName)) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((node) => {
      const fixedValue = fixString(node.nodeValue);

      if (fixedValue !== node.nodeValue) {
        node.nodeValue = fixedValue;
      }
    });
  }

  function scheduleFix(root) {
    if (scheduledRoots.has(root)) {
      return;
    }

    scheduledRoots.add(root);
    (window.requestAnimationFrame || window.setTimeout)(() => {
      scheduledRoots.delete(root);
      fixRoot(root);
    });
  }

  function observeRoot(root) {
    if (!root || observedRoots.has(root)) {
      return;
    }

    observedRoots.add(root);
    fixRoot(root);

    new MutationObserver(() => scheduleFix(root)).observe(root, {
      attributes: true,
      attributeFilter: attributeNames,
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  observeRoot(document.documentElement);
})();
